import React, { useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, X, TriangleAlert, Scale, Search, Loader2, Lock, Clock } from "lucide-react";

// Court-ready call closure. Police calls REQUIRE a disposition code + narrative (CJIS/NIBRS).
export default function CloseCallDisposition({ open, onClose, incidentId, incident, onDone }) {
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [narr, setNarr] = useState("");
  const [busy, setBusy] = useState(false);
  const [closeAt, setCloseAt] = useState("");
  const [lastClear, setLastClear] = useState(null);
  const toLocal = (iso) => { const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

  useEffect(() => {
    if (!open) return;
    setSel(null); setNarr(""); setQ(""); setCloseAt(""); setLastClear(null);
    client.get("/disposition-codes").then((r) => setGroups(r.data)).catch(() => {});
    if (incidentId) client.get(`/incidents/${incidentId}/last-clear`).then((r) => {
      setLastClear(r.data.last_clear);
      setCloseAt(toLocal(r.data.last_clear?.ts || r.data.now));
    }).catch(() => {});
  }, [open, incidentId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return groups;
    return groups
      .map((g) => ({ ...g, codes: g.codes.filter((c) =>
        [c.code, c.label, c.desc].join(" ").toLowerCase().includes(term)) }))
      .filter((g) => g.codes.length);
  }, [groups, q]);

  if (!open) return null;
  const isPolice = incident?.discipline === "police";

  const fileClose = async () => {
    if (!sel) { toast.error("Select a disposition code"); return; }
    if (!narr.trim()) { toast.error("Disposition narrative is required (court record)"); return; }
    setBusy(true);
    try {
      const r = await client.post(`/incidents/${incidentId}/status`, {
        status: "closed", disposition_code: sel.code, disposition_narrative: narr.trim(),
        closed_at: closeAt ? new Date(closeAt).toISOString() : null });
      toast.success(`Call closed — ${sel.code} filed`);
      if (r.data?.rms_case) {
        toast.info(`REPORT REQUIRED — RMS draft ${r.data.rms_case} auto-created for the reporting officer`, { duration: 7000 });
      }
      onDone?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Close failed"); }
    finally { setBusy(false); }
  };

  const saveOnly = async () => {
    if (!sel && !narr.trim()) { toast.error("Nothing to save"); return; }
    setBusy(true);
    try {
      await client.post(`/incidents/${incidentId}/events`, {
        type: "note", message: `DISPOSITION DRAFT ${sel ? sel.code : ""}: ${narr.trim()}`.trim() });
      toast.success("Disposition draft saved to event log");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="disposition-modal">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-[#0d0e11] border border-[#2C2D35] shadow-2xl">
        {/* header */}
        <div className="shrink-0 flex items-center gap-3 px-5 py-4 border-b border-[#2C2D35]">
          <div className="w-9 h-9 flex items-center justify-center bg-[#10B981]/15 border border-[#10B981]/40">
            <ShieldCheck size={18} className="text-[#10B981]" />
          </div>
          <div className="min-w-0">
            <div className="font-head font-bold text-white tracking-tight leading-none">CLOSE CALL — DISPOSITION</div>
            <div className="font-mono text-[10px] text-[#9CA3AF] mt-1 truncate">
              {incident?.cc_number} · {incident?.call_type_label}
            </div>
          </div>
          <button data-testid="disposition-close-x" onClick={onClose} className="ml-auto text-[#9CA3AF] hover:text-white"><X size={20} /></button>
        </div>

        {/* CJIS warning */}
        <div className="shrink-0 mx-5 mt-4 px-3 py-2 border border-[#F59E0B]/40 bg-[#F59E0B]/10 flex gap-2">
          <TriangleAlert size={14} className="text-[#F59E0B] shrink-0 mt-0.5" />
          <p className="font-mono text-[10px] leading-relaxed text-[#F59E0B]">
            CJIS/NENA REQUIREMENT: A valid APCO disposition code and narrative are required before call closure.
            This record is court-admissible and subject to audit. False or missing dispositions violate NIBRS reporting standards.
          </p>
        </div>

        {/* search */}
        <div className="shrink-0 px-5 pt-4">
          <div className="flex items-center gap-2 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 focus-within:border-[#3B82F6]">
            <Search size={14} className="text-[#6B7280]" />
            <input data-testid="disposition-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search disposition codes…" className="flex-1 bg-transparent outline-none text-sm font-mono text-white placeholder:text-[#6B7280]" />
          </div>
        </div>

        {/* code list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4" data-testid="disposition-list">
          {filtered.map((g) => (
            <div key={g.group}>
              <div className="font-mono text-[10px] text-[#6B7280] uppercase tracking-wider mb-2">{g.group}</div>
              <div className="space-y-1.5">
                {g.codes.map((c) => {
                  const active = sel?.code === c.code;
                  return (
                    <button key={c.code} data-testid={`disposition-code-${c.code}`} onClick={() => setSel(c)}
                      className={`w-full flex items-center gap-4 px-4 py-3 text-left border transition-colors ${
                        active ? "border-[#10B981] bg-[#10B981]/10" : "border-[#2C2D35] bg-[#141518] hover:border-[#3B82F6]"}`}>
                      <span className={`font-mono text-xs font-bold w-10 shrink-0 ${active ? "text-[#10B981]" : "text-[#9CA3AF]"}`}>{c.code}</span>
                      <span className="min-w-0">
                        <span className="block font-head text-sm text-white leading-tight">{c.label}</span>
                        <span className="block font-mono text-[11px] text-[#9CA3AF] mt-0.5">{c.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="font-mono text-xs text-[#6B7280] text-center py-6">No matching disposition codes.</div>}
        </div>

        {/* narrative */}
        <div className="shrink-0 px-5 pb-3 border-t border-[#2C2D35] pt-3">
          <div className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
            <Scale size={12} /> Disposition Narrative (Court-Ready)
          </div>
          <textarea data-testid="disposition-narrative" value={narr} onChange={(e) => setNarr(e.target.value)} rows={3}
            placeholder="Enter disposition narrative: what was found, action taken, outcome, reporting party advised, etc. This text is part of the official court record."
            className="w-full bg-[#141518] border border-[#2C2D35] px-3 py-2 text-sm outline-none focus:border-[#3B82F6] font-mono text-white placeholder:text-[#4B5563]" />
        </div>

        {/* effective close time */}
        <div className="shrink-0 px-5 pb-3 border-t border-[#2C2D35] pt-3">
          <div className="font-mono text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
            <Clock size={12} /> Call Closed At (effective)
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="close-time-input" type="datetime-local" value={closeAt} onChange={(e) => setCloseAt(e.target.value)} max={toLocal(new Date().toISOString())}
              className="bg-[#141518] border border-[#2C2D35] px-3 py-1.5 text-sm outline-none focus:border-[#3B82F6] font-mono text-white" />
            {lastClear && <button type="button" data-testid="close-time-use-clear" onClick={() => setCloseAt(toLocal(lastClear.ts))} className="px-2.5 py-1.5 text-[10px] font-head font-bold border border-[#10B981]/60 text-[#6EE7B7] hover:bg-[#10B981]/15">USE LAST UNIT CLEAR {lastClear.ts.slice(11, 16)}Z</button>}
            <button type="button" data-testid="close-time-use-now" onClick={() => setCloseAt(toLocal(new Date().toISOString()))} className="px-2.5 py-1.5 text-[10px] font-head font-bold border border-[#2C2D35] text-[#9CA3AF] hover:text-white">NOW</button>
            <span className="font-mono text-[9px] text-[#6B7280]">{lastClear ? `Defaulted to last unit clear — ${lastClear.message}` : "No unit clear found — defaults to now. Edit if units cleared earlier."}</span>
          </div>
        </div>

        {/* footer */}
        <div className="shrink-0 flex items-center gap-3 px-5 py-3 border-t border-[#2C2D35] bg-[#0a0b0d]">
          <span className="font-mono text-[10px] text-[#9CA3AF] flex items-center gap-1.5" data-testid="disposition-status">
            <Lock size={12} /> {sel ? `${sel.code} — ${sel.label}` : "NO DISPOSITION SELECTED"}
          </span>
          <button data-testid="disposition-save-only" onClick={saveOnly} disabled={busy}
            className="ml-auto px-4 py-2 text-xs font-head bg-[#1E1F24] border border-[#2C2D35] text-[#9CA3AF] hover:text-white transition-colors disabled:opacity-50">
            SAVE ONLY
          </button>
          <button data-testid="disposition-file-close" onClick={fileClose} disabled={busy || !sel || !narr.trim()}
            className="px-5 py-2 text-xs font-head bg-[#DC2626] text-white flex items-center gap-1.5 hover:bg-[#b91c1c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
            CLOSE CALL — FILE DISPOSITION
          </button>
        </div>
        {isPolice && <div className="shrink-0 px-5 pb-2 font-mono text-[9px] text-[#6B7280] bg-[#0a0b0d]">Police call — closure blocked until a disposition code and narrative are filed.</div>}
      </div>
    </div>
  );
}
